import { useEffect, useState } from "react";
import { api, BRL, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Landmark, TrendingUp, TrendingDown, PiggyBank, Plus, Trash2, Loader2, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", description: "", amount: "", date: new Date().toISOString().slice(0, 10) };

export default function Caixa() {
  const [summary, setSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    api.get("/cash-summary").then((r) => setSummary(r.data));
    api.get("/incomes").then((r) => setIncomes(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = (preset) => { setForm({ ...empty, ...preset }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.amount) return toast.error("Preencha nome e valor");
    setSaving(true);
    try {
      await api.post("/incomes", { ...form, amount: parseFloat(form.amount) });
      toast.success("Entrada adicionada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.delete(`/incomes/${toDelete.id}`);
    setToDelete(null);
    toast.success("Entrada removida");
    load();
  };

  const cards = [
    { label: "Saldo atual", value: BRL(summary?.balance || 0), icon: Landmark, tone: (summary?.balance || 0) >= 0 ? "text-primary" : "text-destructive", testid: "cash-balance" },
    { label: "Total de Entradas", value: BRL(summary?.total_income || 0), icon: TrendingUp, tone: "text-primary", testid: "cash-income" },
    { label: "Total Pago (saídas)", value: BRL(summary?.total_paid || 0), icon: TrendingDown, tone: "text-amber-600 dark:text-amber-500", testid: "cash-paid" },
    { label: "Saldo previsto", value: BRL(summary?.projected_balance || 0), icon: PiggyBank, tone: (summary?.projected_balance || 0) >= 0 ? "" : "text-destructive", testid: "cash-projected" },
  ];

  return (
    <div className="fade-up space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">Caixa</h1>
          <p className="text-muted-foreground mt-1">Entradas, abertura de caixa e saldo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="open-cash-button" className="font-semibold gap-2" onClick={() => openNew({ name: "Abertura de caixa" })}>
            <ArrowDownCircle className="h-4 w-4" /> Abertura de caixa
          </Button>
          <Button data-testid="add-income-button" className="font-semibold gap-2" onClick={() => openNew({})}>
            <Plus className="h-4 w-4" /> Nova entrada
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c) => (
          <Card key={c.label} className="p-6 border-border/60 shadow-none rounded-md" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground font-medium">{c.label}</span>
              <c.icon className={`h-4 w-4 ${c.tone || "text-muted-foreground"}`} aria-hidden />
            </div>
            <p className={`font-head text-3xl font-extrabold tracking-tight tnum ${c.tone}`}>{c.value}</p>
          </Card>
        ))}
      </div>

      <Card className="border-border/60 shadow-none rounded-md overflow-hidden">
        <div className="p-6 pb-3">
          <h2 className="font-head text-lg font-bold tracking-tight">Entradas</h2>
          <p className="text-sm text-muted-foreground">Aberturas de caixa e recebimentos</p>
        </div>
        {incomes.length === 0 ? (
          <div className="py-16 text-center" data-testid="incomes-empty">
            <PiggyBank className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" aria-hidden />
            <p className="text-muted-foreground">Nenhuma entrada registrada.</p>
            <Button variant="link" className="text-primary" onClick={() => openNew({ name: "Abertura de caixa" })}>Registrar abertura de caixa</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {incomes.map((inc) => (
                <TableRow key={inc.id} data-testid={`income-row-${inc.id}`}>
                  <TableCell>
                    <span className="font-medium">{inc.name}</span>
                    {inc.description && <p className="text-xs text-muted-foreground truncate max-w-[260px]">{inc.description}</p>}
                  </TableCell>
                  <TableCell>{new Date(inc.date).toLocaleDateString("pt-BR")}</TableCell>
                  <TableCell className="text-right font-semibold tnum text-primary">{BRL(inc.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-income-${inc.id}`} onClick={() => setToDelete(inc)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="income-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl tracking-tight">Nova entrada</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nome</Label><Input data-testid="income-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Abertura de caixa" /></div>
            <div className="space-y-2"><Label>Descrição</Label><Textarea data-testid="income-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Opcional" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Valor (R$)</Label><Input type="number" step="0.01" data-testid="income-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" className="tnum" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" data-testid="income-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="income-save-button" className="font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-income-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover entrada?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" ({BRL(toDelete?.amount || 0)}) será removida do caixa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-income">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
